import type { CSSProperties, KeyboardEvent } from "react";
import { cn } from "@/lib/utils";
import { SHOWCASE_SCREENS, type ShowcaseScreenId } from "./productShowcaseConfig";

export interface ScreenCopy {
  title: string;
  description: string;
}

interface NavProps {
  screens: ShowcaseScreenId[];
  activeId: ShowcaseScreenId;
  copy: (id: ShowcaseScreenId) => ScreenCopy;
  panelId: string;
  onSelect: (id: ShowcaseScreenId) => void;
}

const STEP: Record<string, number> = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 };

/**
 * Arrow keys, Home and End move between the buttons of one navigation
 * (`data-ps-nav` spans both card columns) and select the screen they land on.
 */
function onNavKey(event: KeyboardEvent<HTMLElement>, onSelect: (id: ShowcaseScreenId) => void) {
  const button = (event.target as HTMLElement).closest<HTMLButtonElement>("button[data-screen]");
  const nav = button?.dataset.psNav;
  const root = button?.closest("[data-ps-root]");
  if (!button || !nav || !root) return;
  const buttons = Array.from(root.querySelectorAll<HTMLButtonElement>(`button[data-ps-nav="${nav}"]`));
  const index = buttons.indexOf(button);
  let next: number | null = null;
  if (event.key in STEP) next = (index + STEP[event.key] + buttons.length) % buttons.length;
  if (event.key === "Home") next = 0;
  if (event.key === "End") next = buttons.length - 1;
  if (next === null) return;
  event.preventDefault();
  buttons[next].focus();
  onSelect(buttons[next].dataset.screen as ShowcaseScreenId);
}

/** One column of feature cards beside the device (wide screens). */
export function FeatureCards({
  screens,
  activeId,
  copy,
  panelId,
  onSelect,
  label,
  autoplay,
  playing,
  clockKey,
  clockMs,
  className,
}: NavProps & {
  label: string;
  autoplay: boolean;
  playing: boolean;
  clockKey: string;
  clockMs: number;
  className?: string;
}) {
  const timed: CSSProperties = {
    animationDuration: `${clockMs}ms`,
    animationPlayState: playing ? "running" : "paused",
  };

  return (
    <div role="group" aria-label={label} className={cn("ps-cards", className)} onKeyDown={(event) => onNavKey(event, onSelect)}>
      {screens.map((id) => {
        const Icon = SHOWCASE_SCREENS[id].icon;
        const text = copy(id);
        const active = id === activeId;
        return (
          <button
            key={id}
            type="button"
            className="ps-card"
            aria-pressed={active}
            aria-controls={panelId}
            data-screen={id}
            data-ps-nav="cards"
            onClick={() => onSelect(id)}
            data-testid={`product-showcase-card-${id}`}
          >
            <span className="ps-card-icon" aria-hidden="true">
              <Icon />
            </span>
            <span className="ps-card-title">{text.title}</span>
            <span className="ps-card-desc">{text.description}</span>
            {active && autoplay ? (
              <span key={clockKey} className="ps-card-progress" style={timed} aria-hidden="true" />
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

/** Pill buttons under the device (phones and tablets); scrolls sideways on phones. */
export function FeatureChips({ screens, activeId, copy, panelId, onSelect, label }: NavProps & { label: string }) {
  return (
    <div role="group" aria-label={label} className="ps-chips" onKeyDown={(event) => onNavKey(event, onSelect)}>
      {screens.map((id) => {
        const Icon = SHOWCASE_SCREENS[id].icon;
        return (
          <button
            key={id}
            type="button"
            className="ps-chip"
            aria-pressed={id === activeId}
            aria-controls={panelId}
            data-screen={id}
            data-ps-nav="chips"
            onClick={(event) => {
              onSelect(id);
              const still = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
              event.currentTarget.scrollIntoView({ block: "nearest", inline: "center", behavior: still ? "auto" : "smooth" });
            }}
            data-testid={`product-showcase-chip-${id}`}
          >
            <Icon aria-hidden="true" />
            {copy(id).title}
          </button>
        );
      })}
    </div>
  );
}
